import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import "./AppFrame.scss";

// Fontaine M27 — AppFrame: the one page frame. Laptop (≥ 900px): sidebar column + content column;
// phone: single column with the bottom tab bar. Owns the scroll container, the skip link, the
// <main> landmark and the sidebar collapsed state (remembered per user).

const KEY = "rc-sidebar-collapsed";
const FrameCtx = createContext({ collapsed: false, setCollapsed: () => {}, toggleCollapsed: () => {} });
export const useFrame = () => useContext(FrameCtx);

const readCollapsed = () => {
  try { return window.localStorage.getItem(KEY) === "1"; } catch { return false; }
};

/**
 * @param {ReactNode} sidebar      laptop navigation (AppSidebar)
 * @param {ReactNode} topbar       laptop top row (AppTopbar)
 * @param {ReactNode} mobileTopbar phone top row
 * @param {ReactNode} bottomBar    phone bottom tab bar
 * @param {ReactNode} overlays     banners / widgets rendered above the routes
 */
export default function AppFrame({ sidebar, topbar, mobileTopbar, bottomBar, overlays, children }) {
  const [collapsed, setCollapsedState] = useState(readCollapsed);
  const setCollapsed = useCallback((v) => {
    setCollapsedState(v);
    try { window.localStorage.setItem(KEY, v ? "1" : "0"); } catch { /* private mode */ }
  }, []);
  const toggleCollapsed = useCallback(() => setCollapsed(!collapsed), [collapsed, setCollapsed]);
  const ctx = useMemo(() => ({ collapsed, setCollapsed, toggleCollapsed }), [collapsed, setCollapsed, toggleCollapsed]);

  // Keyboard: "[" toggles the sidebar (not while typing).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "[" || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      toggleCollapsed();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggleCollapsed]);

  return (
    <FrameCtx.Provider value={ctx}>
      <div className={`ft-frame${collapsed ? " is-collapsed" : ""}`}>
        <a className="ft-frame__skip" href="#main">Skip to content</a>
        {overlays}
        {sidebar ? <div className="ft-frame__sidebar">{sidebar}</div> : null}
        <div className="ft-frame__body">
          {topbar ? <div className="ft-frame__topbar">{topbar}</div> : null}
          {mobileTopbar ? <div className="ft-frame__mobile-topbar">{mobileTopbar}</div> : null}
          <main id="main" className="ft-frame__main" tabIndex={-1}>
            {children}
          </main>
        </div>
        {bottomBar ? <div className="ft-frame__bottombar">{bottomBar}</div> : null}
      </div>
    </FrameCtx.Provider>
  );
}
